import React from 'react';
import { useTranslation } from 'react-i18next';
import { Unplug } from 'lucide-react';

type Props = {
  // Nom affiche dans le fallback (le label du manifeste).
  name: string;
  children: React.ReactNode;
};

type State = {
  crashed: boolean;
};

function RemoteUnavailable({ name }: { name: string }) {
  const { t } = useTranslation('common');
  // Le label du manifeste est une clé i18next « ns:clé » (ex. navigation:inProgressTransactions) :
  // on le résout en libellé humain, sinon l'utilisateur lirait la clé brute dans la phrase.
  const displayName = t(name, { defaultValue: name });

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      {/* Panneau navy signature (même voix que le bandeau client du détail) : l'état
          d'erreur reste dans la marque plutôt que dans l'alarme. */}
      <div className="animate-in fade-in-0 slide-in-from-bottom-2 w-full max-w-md rounded-2xl bg-primary p-8 text-center shadow-[0_10px_30px_-8px_rgba(40,61,91,0.35)] duration-300 motion-reduce:animate-none">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-secondaryYellow/15">
          <Unplug className="h-7 w-7 text-secondaryYellow" aria-hidden />
        </div>
        <h2 className="font-merriweather text-xl font-medium text-white">{t('mfeUnavailableTitle')}</h2>
        <p className="mx-auto mt-2 max-w-xs font-roboto text-sm leading-relaxed text-white/70">
          {t('mfeUnavailableBody', { name: displayName })}
        </p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          {/* Rechargement complet (pas de navigation SPA) : repartir d'un état sain
              après un module qui a planté ou n'a pas chargé. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-secondaryYellow px-4 py-2 font-roboto text-sm font-semibold text-primary transition-colors hover:bg-secondaryYellow/85 focus-visible:ring-2 focus-visible:ring-secondaryYellow/60 focus-visible:outline-hidden"
          >
            {t('mfeUnavailableReload')}
          </button>
          <a
            href="/"
            className="rounded-lg border border-white/20 px-4 py-2 font-roboto text-sm font-medium text-white transition-colors hover:border-white/40 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-secondaryYellow/60 focus-visible:outline-hidden"
          >
            {t('mfeUnavailableHome')}
          </a>
        </div>
      </div>
    </div>
  );
}

// Isole un remote : s'il jette au chargement ou au rendu, seul son
// emplacement affiche le fallback, le reste du host continue de tourner.
class RemoteErrorBoundary extends React.Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[MFE] Le remote "${this.props.name}" a planté :`, error, info);
  }

  render() {
    if (this.state.crashed) return <RemoteUnavailable name={this.props.name} />;
    return this.props.children;
  }
}

export default RemoteErrorBoundary;
